<template>
	<div>
		<PageHeader
			:data="{
				name: route,
				breadcrumbs: [{ name: 'Home', to: '/' }],
			}"
		/>

		<div v-if="!error" class="row mb-3">
			<div class="col-md-12 actions my-3">
				<router-link :to="'clients/notifications'" class="btn btn-primary"> <font-awesome-icon :icon="['fas', 'bell']" class="me-1" /> Notifications </router-link>
				<router-link :to="'clients/statistics'" class="btn btn-outline-primary ms-2">
					<font-awesome-icon :icon="['fas', 'chart-line']" class="me-1" /> View Client Statistics
				</router-link>
				<router-link :to="'clients/recurrence'" class="btn btn-outline-primary ms-2">
					<font-awesome-icon :icon="['fas', 'sync']" class="me-1" /> Client Recurrence
				</router-link>
				<router-link :to="'clients/cancellations'" class="btn btn-outline-primary ms-2">
					<font-awesome-icon :icon="['fas', 'circle-question']" class="me-1" /> Cancellation Reasons
				</router-link>
			</div>
			<!-- Filters -->
			<div class="col-12 mb-2">
				<div class="card">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center">
							<div class="header d-flex align-items-center">
								<h4 class="p-0 m-0 me-2"><font-awesome-icon :icon="['fas', 'filter']" class="text-primary me-1" /> Filters</h4>
								<button v-if="Object.values(this.params).length > 4 && searchActive === ''" @click="clearFilters()" class="btn btn-sm btn-danger text-white me-2">
									Clear
									<font-awesome-icon :icon="['fas', 'times']" />
								</button>
								<button v-if="!searchSaving && haveParamsToSave() && searchActive === ''" @click="searchSaving = true" class="btn btn-sm btn-primary">
									Save search
								</button>
								<div v-if="searchSaving && searchActive === ''" class="save-search d-flex">
									<form @submit.prevent="storeSearch" class="d-flex">
										<input type="text" v-model="searchName" class="form-control form-control-sm ms-2" placeholder="enter search name" required />
										<button type="submit" class="btn btn-sm btn-primary ms-1">Save</button>
										<a role="button" @click="searchSaving = false" class="btn btn-sm ms-1"> Cancel </a>
									</form>
								</div>
								<span v-if="searchActive !== ''" class="badge bg-success"
									>{{ searchActive }} <font-awesome-icon role="button" @click="clearFilters()" class="ms-2" :icon="['fas', 'times']"
								/></span>
							</div>
							<div class="action-buttons">
								<button
									data-bs-toggle="offcanvas"
									data-bs-target="#offcanvasSearches"
									aria-controls="offcanvasSearches"
									class="btn btn-outline-secondary btn-sm me-2"
								>
									Saved Searches <font-awesome-icon :icon="['fas', 'save']" class="me-1" />
								</button>
							</div>
						</div>

						<LPDataTableFiltersModern v-if="filtersUiMode === 'modern'" :filters="table.filters" v-on:getFilters="getFilters($event.filters)" />
						<LPDataTableFilters v-else :filters="table.filters" v-on:getFilters="getFilters($event.filters)" />
					</div>
				</div>
			</div>

			<!-- Table data -->
			<div class="col-12 mb-2">
				<div class="card">
					<div class="card-body">
						<div class="d-flex justify-content-between align-items-center">
							<div class="header d-flex align-items-center">
								<h4 class="p-0 m-0 me-2 text-capitalize">{{ route }}</h4>
								<a data-bs-toggle="modal" data-bs-target="#create" class="btn btn-outline-primary btn-sm"
									>Add {{ model }} <font-awesome-icon :icon="['fas', 'plus-circle']" class="me-1"
								/></a>
								<router-link
									v-if="authUser.permissions.includes(model.toUpperCase() + '_IMPORT') && false"
									:to="`/${route}/import`"
									class="btn btn-primary btn-sm me-2"
								>
									Import <font-awesome-icon :icon="['fas', 'file-csv']" class="me-1" />
								</router-link>
							</div>

							<div class="action-buttons">
								<button
									data-bs-toggle="offcanvas"
									data-bs-target="#offcanvasTableSettings"
									aria-controls="offcanvasTableSettings"
									class="btn btn-outline-secondary btn-sm"
								>
									Table Settings <font-awesome-icon :icon="['fas', 'cogs']" class="me-1" />
								</button>
							</div>
						</div>
						<table :class="!loading && tableData.length > 0 ? 'table table-hover' : 'table table-hover loading'">
							<LPDataTableThead :thead="table.columns" :userColumns="userColumns" v-on:setOrder="setOrderBy($event.orderBy, $event.direction)" />
							<LPDataTableTbody
								:userColumns="userColumns"
								:authUser="authUser"
								:data="tableData"
								:tbody="table.columns"
								:model="model"
								:route="route"
								:rowInfo="true"
								v-on:showInformationModal="showInformationModal($event)"
								v-on:deleted="getTableData"
							/>
						</table>
						<div v-if="!loading && tableData.length === 0" class="col-md-12 text-center">
							<h4>No data available</h4>
						</div>
						<div v-if="loading && tableData.length === 0" class="d-flex justify-content-center">
							<div class="spinner-border" role="status">
								<span class="visually-hidden">Loading...</span>
							</div>
						</div>
						<div class="table-actions d-flex">
							<button data-bs-toggle="modal" data-bs-target="#exportModal" class="btn btn-success btn-sm me-2">
								Export <font-awesome-icon :icon="['fas', 'file-csv']" class="me-1" />
							</button>
							<button @click="getCampaignClients()" data-bs-toggle="modal" data-bs-target="#campaignModal" class="btn btn-secondary btn-sm me-2">
								Create Campaign <font-awesome-icon :icon="['fas', 'plus-circle']" class="me-1" />
							</button>
						</div>
						<LPDataTablePagination :pagination="pagination" v-on:getItems="getItems($event.page)" />
					</div>
				</div>
			</div>
		</div>

		<div v-else class="row mb-3">
			<div class="col-12">
				<div class="card">
					<div class="card-body">
						<div class="alert alert-danger" role="alert">
							{{ error }}
						</div>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal for creating campaign -->
		<div class="modal fade" id="campaignModal" tabindex="-1" aria-labelledby="campaignModalLabel" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<form @submit.prevent="createCampaign">
						<div class="modal-header">
							<h5 class="modal-title" id="exampleModalLabel">Create Campaign</h5>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div class="modal-body">
							<div class="form-group mb-2">
								<label for="">Campaign Name</label>
								<div class="input-group mb-3">
									<input type="text" class="form-control" placeholder="Campaign name" v-model="newCampaign.name" required />
								</div>
							</div>
							<div class="form-group mb-2">
								<label for="">Description</label>
								<div class="input-group mb-3">
									<textarea class="form-control" placeholder="Campaign description" v-model="newCampaign.description" />
								</div>
							</div>
							<div class="form-group mb-2">
								<label for="">UTM Campaign</label>
								<div class="input-group mb-3">
									<utm-campaign-input v-model="newCampaign.utm_campaign" />
								</div>
							</div>
							<div class="form-group mb-2">
								<label>UTM Content</label>
								<utm-content-input v-model="newCampaign.utm_content" />
							</div>
							<div v-if="!loadingCampaignObjects">
								<span class="badge badge-primary bg-success w-100 py-3"
									><h4 class="m-0 p-0">{{ newCampaign.campaignObjects.length }} clients</h4></span
								>
							</div>
							<div v-else>
								<div class="d-flex justify-content-center">
									<div class="spinner-border" role="status">
										<span class="visually-hidden">Loading...</span>
									</div>
								</div>
							</div>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
							<button type="submit" :disabled="loadingCampaingCreate" class="btn btn-primary">
								<font-awesome-icon :icon="['fas', loadingCampaingCreate ? 'spinner' : 'save']" :class="[loadingCampaingCreate ? 'fa-spin' : '']" /> Create
							</button>
						</div>
					</form>
				</div>
			</div>
		</div>

		<!-- Modal for adding Data -->
		<div class="modal fade" id="create" tabindex="-1" aria-labelledby="createLabel" aria-hidden="true" data-bs-backdrop="static" data-bs-keyboard="false">
			<div class="modal-dialog" :class="addClients ? 'modal-lg' : ''">
				<div class="modal-content">
					<form @submit.prevent="create()">
						<div class="modal-header">
							<h5 class="modal-title" id="exampleModalLabel">
								New {{ model }} 🤗
								<template v-if="newClient.name.length > 3"> - {{ newClient.name }} 🎉</template>
							</h5>
							<button type="button" class="btn-close" data-bs-dismiss="modal" @click="cancelAddingClient()" aria-label="Close"></button>
						</div>
						<div class="modal-body" v-if="!addClients">
							<div class="form-group mb-2">
								<label for="" class="">Type</label>
								<select class="form-select" @change="clearNewClient" v-model="newClient.type" required>
									<option value="">Select type</option>
									<option value="Municipality">🏛️ Municipality</option>
									<option value="Business">👔 Business</option>
									<option value="Non Profit">Non Profit</option>
									<option value="Lake District">🌊 Lake District</option>
									<option value="Fire Department">🔥 Fire Department</option>
									<option value="Police Department">👮 Police Department</option>
									<option value="Chamber">Chamber</option>
									<option value="Other">ℹ️ Other</option>
								</select>
							</div>

							<!-- If type is municipality show municipality search -->
							<div class="mb-2" v-if="newClient.type === 'Municipality'">
								<MunicipalitySearch :selected="null" excludeClients="true" v-on:setMunicipality="setMunicipality" />
							</div>

							<div class="row">
								<div class="col-8">
									<div class="form-group mb-2" v-if="newClient.type !== ''">
										<label for="" class="">Name</label>
										<input type="text" :readonly="newClient.type === 'municipality'" class="form-control" v-model="newClient.name" required />
									</div>
								</div>
								<div class="col-4 d-flex align-items-center" v-if="fips !== ''">
									FIPS
									<a target="_blank" class="ms-1" :href="'https://www.google.rs/search?q=FIPS ' + fips"> {{ fips }}</a>
								</div>
							</div>

							<div class="form-group mb-2" v-if="newClient.type !== ''">
								<label for="" class="">Primary Product</label>
								<select class="form-select" v-model="newClient.primary_product">
									<option value="">Select primary product</option>
									<option value="townweb">Town Web</option>
									<option value="heygov">HeyGov</option>
									<option v-if="newClient.type !== 'municipality'" value="tweak">Tweak</option>
									<option v-if="newClient.type !== 'municipality'" value="hosting">Hosting ony</option>
								</select>
							</div>

							<div class="form-group mb-2" v-if="newClient.type !== '' && newClient.primary_product === 'townweb'">
								<label for="contract_start_at" class="">Contract start date</label>
								<input type="date" class="form-control" v-model="newClient.contract_start_at" />
							</div>

							<div class="form-group mb-2" v-if="newClient.type !== '' && newClient.primary_product === 'heygov'">
								<label for="hg_contract_start_at" class="">Contract start date</label>
								<input type="date" class="form-control" v-model="newClient.hg_contract_start_at" />
							</div>

							<div class="form-group mb-2" v-if="newClient.type !== '' && newClient.primary_product === 'townweb'">
								<label for="" class="">Type</label>
								<select class="form-select" v-model="newClient.contract_type">
									<option value="">Select contract type</option>
									<option value="1 year">1 year</option>
									<option value="2 years">2 years</option>
									<option value="3 years">3 years</option>
									<option value="4 years">4 years</option>
									<option value="5 years">5 years</option>
									<option value="No contract">No contract</option>
								</select>
							</div>

							<div class="form-group mb-2" v-if="newClient.type !== ''">
								<label for="" class="">State</label>
								<input type="text" :readonly="newClient.type === 'municipality'" class="form-control" v-model="newClient.state" />
							</div>

							<div class="form-group mb-2" v-if="newClient.type !== ''">
								<label for="" class="">County</label>
								<input type="text" :readonly="newClient.type === 'municipality'" class="form-control" v-model="newClient.county" />
							</div>

							<div class="form-group mb-2" v-if="newClient.type !== ''">
								<label for="" class="">Address</label>
								<div class="input-group mb-3">
									<input
										type="text"
										class="form-control"
										aria-describedby="edit-address"
										v-model="newClient.address"
										:readonly="newClient.type === 'municipality' && !updateAddressFlag"
									/>
									<button class="btn btn-outline-secondary" @click="updateAddressFlag = !updateAddressFlag" type="button" id="edit-address">Edit</button>
								</div>
							</div>
						</div>
						<div class="modal-body" v-else>
							<template v-if="municipalityContacts.length > 0">
								<h5>Choose or add new primary contact</h5>
								<small
									>ℹ️ Note that if you choose contact from table and update contact data it will also apply updates to Business Intelligence contacts to keep
									contact information fresh with data
								</small>
								<div class="row mt-2">
									<div class="col-3">Name</div>
									<div class="col-4">Email</div>
									<div class="col-3">Phone</div>
									<div class="col-2">Title</div>
								</div>
								<template v-for="contact in municipalityContacts">
									<div
										:key="contact.id"
										class="p-2 shadow-sm rounded contact-item mt-1"
										:class="contact.active ? 'bg-primary text-white' : ''"
										@click="selectContact(contact)"
										role="button"
									>
										<div class="row">
											<div class="col-3">
												<span>{{ contact.first_name }} {{ contact.last_name }}</span>
											</div>
											<div class="col-4">
												<span>{{ contact.email }}</span>
											</div>
											<div class="col-3">
												<span>{{ contact.phone }}</span>
											</div>
											<div class="col-2">
												<span>{{ contact.title }}</span>
											</div>
										</div>
									</div>
								</template>
								<button type="button" class="btn btn-primary my-3" @click="addNewContact()">Add new contact</button>
							</template>
							<div class="adding-updating-contact mt-2" v-if="addContactFlag || updateContactFlag">
								<h5 class="m-0">
									<template v-if="addContactFlag"> Adding primary contact </template>
									<template v-if="updateContactFlag"> Update primary contact </template>
								</h5>
								<div class="row">
									<div class="col-6">
										<label for="">First Name</label>
										<input type="text" class="form-control" v-model="activeContact.first_name" />
									</div>
									<div class="col-6">
										<label for="">Last Name</label>
										<input type="text" class="form-control" v-model="activeContact.last_name" />
									</div>
									<div class="col-6">
										<label for="">Email</label>
										<input type="text" class="form-control" v-model="activeContact.email" />
									</div>
									<div class="col-6">
										<label for="">Phone</label>
										<input type="text" class="form-control" v-model="activeContact.phone" />
									</div>
									<div class="col-6">
										<label for="">Email secondary</label>
										<input type="text" class="form-control" v-model="activeContact.email_secondary" />
									</div>
									<div class="col-6">
										<label for="">Phone secondary</label>
										<input type="text" class="form-control" v-model="activeContact.phone_secondary" />
									</div>
									<div class="col-6">
										<label for="">Address</label>
										<input type="text" class="form-control" v-model="activeContact.address" />
									</div>
									<div class="col-6">
										<label for="">Title</label>
										<input type="text" class="form-control" v-model="activeContact.title" />
									</div>
								</div>
							</div>
						</div>
						<div class="modal-footer">
							<a type="button" @click="cancelAddingClient()" class="btn btn-secondary" data-bs-dismiss="modal"> Close </a>
							<a class="btn btn-secondary" v-if="addClients" @click="addClients = !addClients"> Back </a>
							<a @click="addClientContacts()" type="button" class="btn btn-primary" :disabled="disableNextButton()" v-if="!addClients"> Next </a>
							<button :disabled="disableFinishButton()" type="submit" class="btn btn-primary" v-if="addClients">Finish</button>
						</div>
					</form>
				</div>
			</div>
		</div>

		<!-- Modal for displaying Data -->
		<div class="modal fade" id="dataInfoModal" tabindex="-1" aria-labelledby="createLabel" aria-hidden="true">
			<div class="modal-dialog modal-dialog-centered modal-lg">
				<div class="modal-content">
					<div class="modal-body">
						<div class="row border-bottom mb-2 pb-1 mb-3">
							<div class="col-1">
								<h1>🏛️</h1>
							</div>
							<div class="col-6">
								<h4 class="mb-0">{{ dataInfo.name }}</h4>
								<span>{{ dataInfo.county }}, {{ dataInfo.state }}</span>
							</div>
							<div class="col-5">
								<div class="float-end">
									<a :href="dataInfo.website" target="_blank" class="btn btn-primary">🌎 Website</a>
								</div>
							</div>
						</div>

						<div class="row mb-1">
							<div class="col-6">
								<!-- Email -->
								<div class="client-editable-item mb-2">
									<p class="m-0 p-0 mt-2 fw-bold">Email 📧</p>
									<p class="m-0 p-0">
										<template v-if="dataInfo.email"> {{ dataInfo.email }} </template>
									</p>
								</div>

								<!-- Phone -->
								<div class="client-editable-item mb-2">
									<p class="m-0 p-0 mt-2 fw-bold">Phone ☎️</p>
									<p class="m-0 p-0">
										<template v-if="dataInfo.phone"> {{ dataInfo.phone }} </template>
									</p>
								</div>

								<!-- Physical address -->
								<div class="client-editable-item mb-2">
									<p class="m-0 p-0 mt-2 fw-bold">Physical address 📍</p>
									<p class="m-0 p-0">
										<template v-if="dataInfo.address"> {{ dataInfo.address }} </template>
									</p>
								</div>

								<!-- Mailing address -->
								<div class="client-editable-item mb-2">
									<p class="m-0 p-0 mt-2 fw-bold">Mailing address 📍</p>
									<p class="m-0 p-0">
										<template v-if="dataInfo.address_mailing">{{ dataInfo.address_mailing }} </template>
									</p>
								</div>
							</div>
							<div class="col-6">
								<div class="d-flex justify-content-between align-items-center mt-2">
									<h5>Contract</h5>
									<div class="client-editable-item mb-2 d-flex justify-content-center align-items-center">
										<p class="m-0 p-0">
											<template v-if="dataInfo.contract_ends_at !== null && dataInfo.contract_ends_at !== '0000-00-00 00:00:00'">
												<span class="badge badge-success bg-success" v-if="dateDifferenceDays(dataInfo.contract_ends_at) >= 30">
													{{ dateDifferenceDays(dataInfo.contract_ends_at) }}
													days
												</span>
												<span
													class="badge badge-warning bg-warning"
													v-if="dateDifferenceDays(dataInfo.contract_ends_at) < 30 && dateDifferenceDays(dataInfo.contract_ends_at) > 0"
												>
													{{ dateDifferenceDays(dataInfo.contract_ends_at) }}
													days
												</span>
												<span v-if="dateDifferenceDays(dataInfo.contract_ends_at) < 0" class="badge badge-danger bg-danger"> Out of contract </span>
											</template>
											<template v-else>
												<span class="badge badge-danger bg-danger"> End date missing </span>
											</template>
										</p>
									</div>
								</div>
							</div>
						</div>

						<div class="row">
							<!-- Town Web -->
							<div class="col-3">
								<div class="client-editable-item mb-2">
									<p class="m-0 p-0 mt-2 fw-bold">Town Web</p>
									<p class="m-0 p-0">
										<template v-if="dataInfo.tw_client">
											<span v-if="dataInfo.tw_client == 'yes'" class="badge badge-success bg-success">Yes</span>
											<span v-else class="badge badge-danger bg-danger">No</span>
										</template>
									</p>
								</div>
							</div>
							<!-- HeyGov -->
							<div class="col-3">
								<div class="client-editable-item mb-2">
									<p class="m-0 p-0 mt-2 fw-bold">HeyGov</p>
									<p class="m-0 p-0">
										<template v-if="dataInfo.hg_client">
											<span v-if="dataInfo.hg_client == 'yes'" class="badge badge-success bg-success">Yes</span>
											<span v-else class="badge badge-danger bg-danger">No</span>
										</template>
									</p>
								</div>
							</div>
							<!-- Tweak -->
							<div class="col-3">
								<div class="client-editable-item mb-2">
									<p class="m-0 p-0 mt-2 fw-bold">Tweak</p>
									<p class="m-0 p-0">
										<template v-if="dataInfo.tk_client">
											<span v-if="dataInfo.tk_client == 'yes'" class="badge badge-success bg-success">Yes</span>
											<span v-else class="badge badge-danger bg-danger">No</span>
										</template>
									</p>
								</div>
							</div>
							<!-- Hosting only -->
							<div class="col-3">
								<div class="client-editable-item mb-2">
									<p class="m-0 p-0 mt-2 fw-bold">Hosting only</p>
									<p class="m-0 p-0">
										<template v-if="dataInfo.hosting">
											<span v-if="dataInfo.hosting == 'yes'" class="badge badge-success bg-success">Yes</span>
											<span v-else class="badge badge-danger bg-danger">No</span>
										</template>
									</p>
								</div>
							</div>
						</div>

						<div class="row mb-1">
							<div class="col-3">
								<p class="m-0 p-0 mt-2 fw-bold">Facebook syndication</p>
								<template v-if="dataInfo.facebook_syndication">
									<span v-if="dataInfo.facebook_syndication === 'no'" class="badge badge-danger bg-danger"> No </span>
									<span v-if="dataInfo.facebook_syndication === 'yes,not implemented'" class="badge badge-primary bg-primary"> Yes, not implemented </span>
									<span v-if="dataInfo.facebook_syndication === 'yes,implemented'" class="badge badge-success bg-success"> Yes, implemented </span>
								</template>
							</div>
							<div class="col-3">
								<p class="m-0 p-0 mt-2 fw-bold">Branded emails</p>
								<template v-if="dataInfo.branded_emails">
									<span v-if="dataInfo.branded_emails === 'no'" class="badge badge-danger bg-danger"> No </span>
									<span v-if="dataInfo.branded_emails === 'yes,not implemented'" class="badge badge-primary bg-primary"> Yes, not implemented </span>
									<span v-if="dataInfo.branded_emails === 'yes,not implemented,archiving'" class="badge badge-primary bg-primary"> Yes, not implemented </span>
									<span v-if="dataInfo.branded_emails === 'yes,implemented'" class="badge badge-success bg-success"> Yes, implemented </span>
									<span v-if="dataInfo.branded_emails === 'yes,implemented,archiving'" class="badge badge-success bg-success"> Yes, implemented </span>
								</template>
							</div>
							<div class="col-3">
								<p class="m-0 p-0 mt-2 fw-bold">Online payments</p>
								<template v-if="dataInfo.online_payments">
									<span v-if="dataInfo.online_payments === 'no'" class="badge badge-danger bg-danger"> No </span>
									<span v-if="dataInfo.online_payments === 'yes,not implemented'" class="badge badge-primary bg-primary"> Yes, not implemented </span>
									<span v-if="dataInfo.online_payments === 'yes,implemented'" class="badge badge-success bg-success"> Yes, implemented </span>
								</template>
							</div>
							<div class="col-3">
								<p class="m-0 p-0 mt-2 fw-bold">Live stream</p>
								<template v-if="dataInfo.live_stream">
									<span v-if="dataInfo.live_stream === 'no'" class="badge badge-danger bg-danger"> No </span>
									<span v-if="dataInfo.live_stream === 'yes,not implemented'" class="badge badge-primary bg-primary"> Yes, not implemented </span>
									<span v-if="dataInfo.live_stream === 'yes,implemented'" class="badge badge-success bg-success"> Yes, implemented </span>
								</template>
							</div>
						</div>

						<div class="mt-4">
							<table :class="'table table-hover mt-1 table-sm'">
								<thead>
									<tr>
										<th>First name</th>
										<th>Last name</th>
										<th>Email</th>
										<th>Phone</th>
										<th>Title</th>
									</tr>
								</thead>
								<tbody>
									<template v-for="contact in dataInfo.contacts">
										<tr :key="'edit-' + contact.id">
											<td class="align-middle">
												{{ contact.first_name }}
											</td>
											<td class="align-middle">
												{{ contact.last_name }}
											</td>
											<td class="align-middle">
												{{ contact.email }}
											</td>
											<td class="align-middle">
												{{ contact.phone }}
											</td>
											<td class="align-middle">
												{{ contact.title }}
											</td>
										</tr>
									</template>
								</tbody>
							</table>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" @click="moreDetails()" class="btn btn-info">More details</button>
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Modal for adding Filter -->
		<div class="modal fade" id="addFilter" tabindex="-1" aria-labelledby="addFilterLabel" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<form @submit.prevent="addFilter">
						<div class="modal-header">
							<h5 class="modal-title" id="exampleModalLabel">Add Filter</h5>
							<small class="ms-2">Adding new filter for {{ this.model }}</small>
							<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
						</div>
						<div class="modal-body text-center">
							<h1>Will come soon 😉</h1>
						</div>
						<div class="modal-footer">
							<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
							<!-- <button type="submit" class="btn btn-primary">Save</button> -->
						</div>
					</form>
				</div>
			</div>
		</div>

		<!-- Modal for export -->
		<div class="modal fade" id="exportModal" tabindex="-1" aria-labelledby="exportModalLabel" aria-hidden="true">
			<div class="modal-dialog">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title" id="exampleModalLabel">Export {{ route }}</h5>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="form-group mb-2">
							<label for="">Export Type</label>
							<select class="form-select" v-model="newExport.type">
								<option value="csv">CSV</option>
							</select>
						</div>
						<div class="form-group mb-2">
							<label for="">File Name</label>
							<div class="input-group mb-3">
								<input type="text" class="form-control" placeholder="File name" v-model="newExport.filename" />
								<span class="input-group-text" id="basic-addon2">.{{ newExport.type }}</span>
							</div>
						</div>
						<div class="form-group">
							<label for="">Columns to export</label>
							<div class="form-check">
								<input class="form-check-input" type="radio" v-model="newExport.columnsType" value="visible" id="visibleColumnsRadio" checked />
								<label class="form-check-label" for="visibleColumnsRadio"> Export visible columns </label>
							</div>
							<div class="my-2" v-if="newExport.columnsType === 'visible'">
								<div class="d-flex flex-wrap">
									<div :key="'column-available-' + column" v-for="column in newExport.exportColumns" class="mb-2 me-1">
										<span class="badge bg-success">{{ column }}</span>
									</div>
								</div>
							</div>
							<div class="form-check">
								<input class="form-check-input" type="radio" v-model="newExport.columnsType" value="custom" id="customColumnsRadio" />
								<label class="form-check-label" for="customColumnsRadio"> Export custom columns </label>
							</div>
							<div class="my-2" v-if="newExport.columnsType === 'custom'">
								<div class="d-flex flex-wrap">
									<small class="w-100 mb-1">ℹ️ Click on column to include/exclude it from export</small>
									<div :key="'column-available-' + column" v-for="column in newExport.availableColumns" class="mb-2 me-1">
										<span role="button" @click="toggleExportColumn(column)" v-if="newExport.exportColumns.includes(column)" class="badge bg-success">{{
											column
										}}</span>
										<span role="button" @click="toggleExportColumn(column)" v-else class="badge bg-secondary">{{ column }}</span>
									</div>
								</div>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
						<button v-if="!loadExporting && exportingFile === ''" @click="exportData()" type="submit" class="btn btn-primary">Create Export</button>
						<a v-if="!loadExporting && exportingFile !== ''" @click="downloadExport()" :href="exportingFile" class="btn btn-primary" download> Download </a>

						<button v-if="loadExporting" class="btn btn-primary" type="button" disabled>
							<span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
							Loading...
						</button>
					</div>
				</div>
			</div>
		</div>

		<!-- Offcanvas for Searches -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasSearches" aria-labelledby="offcanvasRightLabel">
			<div class="offcanvas-header">
				<h5 id="offcanvasRightLabel">Saved searches</h5>
				<button type="button" class="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body">
				<h4 class="m-0">Your saved searches</h4>
				<p class="p-0"><small>ℹ️ Click on search to apply search filters to table</small></p>
				<div class="table-columns-settings">
					<div class="d-flex flex-wrap">
						<div :key="'user-searches-' + search.id" v-for="search in searches" class="mb-2 me-1">
							<span
								data-bs-toggle="offcanvas"
								data-bs-target="#offcanvasSearches"
								aria-controls="offcanvasSearches"
								role="button"
								@click="applySearch(search)"
								class="badge bg-success"
								>{{ search.name }}</span
							>
						</div>
					</div>
				</div>

				<small>👨‍🏫 Your saved searches for fast applying filters, you can add important ones to display above table (🚧 coming soon)</small>
			</div>
		</div>
		<!-- Offcanvas for Table Settings -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasTableSettings" aria-labelledby="offcanvasRightLabel">
			<div class="offcanvas-header">
				<h5 id="offcanvasRightLabel">Table settings</h5>
				<button type="button" class="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body">
				<h4 class="m-0">Display columns</h4>
				<p class="p-0"><small>ℹ️ Select which columns to display in table</small></p>
				<div class="table-columns-settings">
					<div class="d-flex flex-wrap">
						<div :key="'column-available-' + column" v-for="column in availableColumns" class="mb-2 me-1">
							<span role="button" @click="toggleUserColumn(column)" v-if="userColumns.includes(column)" class="badge bg-success">{{ column }}</span>
							<span role="button" @click="toggleUserColumn(column)" v-else class="badge bg-secondary">{{ column }}</span>
						</div>
					</div>
				</div>

				<small>👨‍🏫 Selected columns are automatically saved to your browser storage and will be used again when you come on this page</small>

				<h4 class="mt-3">Display {{ this.route }} per page</h4>
				<p class="p-0">
					<small>ℹ️ Select how many {{ this.route }} to display per page in table</small>
				</p>
				<div class="form-group w-75">
					<select v-model="userPerPage" @change="setUserPerPage()" class="form-select">
						<option value="10">10 per page</option>
						<option value="15">15 per page</option>
						<option value="20">20 per page</option>
						<option value="30">30 per page</option>
						<option value="50">50 per page</option>
						<option value="100">100 per page</option>
					</select>
				</div>
			</div>
		</div>
		<!-- Modal for displaying all information -->
		<div v-if="authUser.permissions.includes('CLIENT_ACCESS')" class="modal fade" id="clientInfo" tabindex="-1" aria-labelledby="clientInfoLabel" aria-hidden="true">
			<div class="modal-dialog modal-lg">
				<div class="modal-content">
					<div class="modal-header">
						<h5 class="modal-title">Log</h5>
						<small class="ms-2">Client Info</small>
						<button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
					</div>
					<div class="modal-body">
						<div class="row">
							<div class="col-12">
								<table class="table table-hover">
									<tbody>
										<template v-for="(prop, index) in clientInfo">
											<tr :key="index" v-if="index !== 'updated_at'">
												<td>
													<b class="text-muted">{{ index }}</b>
												</td>
												<td class="text-break">{{ prop }}</td>
											</tr>
										</template>
									</tbody>
								</table>
							</div>
						</div>
					</div>
					<div class="modal-footer">
						<button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Close</button>
					</div>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import PageHeader from '@/components/layout/PageHeader'
import UtmCampaignInput from '@/components/common/UtmCampaignInput'
import UtmContentInput from '@/components/common/UtmContentInput'
import { mapState } from 'vuex'
import restApi from '../../api/index.js'
import { Modal } from 'bootstrap'
import { handleErrors } from '../../actions/helpers'
import { differenceInDays } from 'date-fns'
import LPDataTablePagination from '@/components/lp-data-table/LPDataTablePagination'
import LPDataTableFilters from '@/components/lp-data-table/LPDataTableFilters'
import LPDataTableFiltersModern from '@/components/lp-data-table/LPDataTableFiltersModern'
import LPDataTableThead from '@/components/lp-data-table/LPDataTableThead'
import LPDataTableTbody from '@/components/lp-data-table/LPDataTableTbody'
import MunicipalitySearch from '@/components/municipality/MunicipalitySearch'

export default {
	name: 'Clients',
	components: {
		UtmCampaignInput, UtmContentInput,
		PageHeader,
		LPDataTablePagination,
		LPDataTableFilters,
		LPDataTableFiltersModern,
		LPDataTableThead,
		LPDataTableTbody,
		MunicipalitySearch,
	},
	data() {
		return {
			route: 'clients',
			model: 'client',
			tableData: [],
			loading: false,
			error: '',
			newTableEntry: {},
			$createModal: null,
			$exportModal: null,
			$addFilterModal: null,
			pagination: {},
			page: this.$route.query.page || 1,
			orderBy: this.$route.query.orderBy || 'id',
			direction: this.$route.query.direction || 'asc',
			userPerPage: localStorage.getItem('client-per-page') || 20,
			filtersUiMode: 'default',
			filtersUiSettings: {
				default: 'default',
				overrides: {},
			},
			table: {
				columns: [
					{
						name: 'id',
						label: 'ID',
						sort: true,
						type: 'default',
					},
					{
						name: 'type',
						label: 'Type',
						sort: true,
						type: 'default',
					},
					{
						name: 'name',
						label: 'Name',
						sort: true,
						type: 'default',
					},
					{
						name: 'cms_version',
						label: 'Cms',
						sort: true,
						type: 'default',
					},
					{
						name: 'state',
						label: 'State',
						sort: true,
						type: 'default',
					},
					{
						name: 'state_abb',
						label: 'State short',
						sort: true,
						type: 'default',
					},
					{
						name: 'county',
						label: 'County',
						sort: false,
						type: 'default',
					},
					{
						name: 'city',
						label: 'City',
						sort: false,
						type: 'default',
					},
					{
						name: 'address',
						label: 'Address',
						sort: false,
						type: 'default',
					},
					{
						name: 'zip',
						label: 'ZIP',
						sort: false,
						type: 'default',
					},
					{
						name: 'office',
						label: 'Office',
						sort: false,
						type: 'default',
					},

					{
						name: 'phone',
						label: 'Phone',
						sort: true,
						type: 'default',
					},

					{
						name: 'population',
						label: 'Population',
						sort: true,
						type: 'default',
					},

					{
						name: 'primary_product',
						label: 'Primary product',
						sort: true,
						type: 'default',
					},

					{
						name: 'quote_url',
						label: 'Quote URL',
						sort: false,
						type: 'link',
					},

					{
						name: 'website',
						label: 'Website',
						sort: false,
						type: 'link',
					},
					{
						name: 'email',
						label: 'Email',
						sort: false,
						type: 'default',
					},
					{
						name: 'status',
						label: 'Status',
						sort: true,
						type: 'custom',
						style: ' <span class="badge badge-primary ms-2 code2">code1</badge>',
						values: [
							{
								name: 'code1',
								type: 'conditions',
								value: 'status',
								conditions: [
									{
										value: 'active',
										replace: 'active',
									},
									{
										value: 'suspended',
										replace: 'suspended',
									},
									{
										value: 'deleted',
										replace: 'Lost customer',
									},
								],
							},

							{
								name: 'code2',
								type: 'class',
								value: 'status',
								conditions: [
									{
										value: 'active',
										replace: 'bg-success',
									},
									{
										value: 'suspended',
										replace: 'bg-warning',
									},
									{
										value: 'deleted',
										replace: 'bg-danger',
									},
								],
							},
						],
					},
					{
						name: 'contract_type',
						label: 'Contract Type',
						sort: false,
						type: 'default',
					},

					{
						name: 'tw_client',
						label: 'Town Web',
						sort: true,
						type: 'boolean',
						yes: 'yes',
						no: 'no',
					},

					{
						name: 'tk_client',
						label: 'Tweak',
						sort: true,
						type: 'boolean',
						yes: 'yes',
						no: 'no',
					},

					{
						name: 'hg_client',
						label: 'HeyGov',
						sort: true,
						type: 'boolean',
						yes: 'yes',
						no: 'no',
					},

					{
						name: 'heygov_311',
						label: 'HeyGov 311',
						sort: true,
						type: 'default',
					},

					{
						name: 'heygov_license',
						label: 'HeyGov License',
						sort: true,
						type: 'default',
					},

					{
						name: 'heygov_reserve',
						label: 'HeyGov Reserve',
						sort: true,
						type: 'default',
					},

					{
						name: 'heygov_pay',
						label: 'HeyGov Pay',
						sort: true,
						type: 'default',
					},

					{
						name: 'heygov_clerk_minutes',
						label: 'HeyGov ClerkMinutes',
						sort: true,
						type: 'default',
					},

					{
						name: 'hosting',
						label: 'Hosting',
						sort: true,
						type: 'boolean',
						yes: 'yes',
						no: 'no',
					},

					{
						name: 'branded_emails',
						label: 'Branded emails',
						sort: true,
						type: 'boolean',
						yes: 'yes',
						no: 'no',
					},

					{
						name: 'online_payments',
						label: 'Online payments',
						sort: true,
						type: 'boolean',
						yes: 'yes',
						no: 'no',
					},

					{
						name: 'free_redesign',
						label: 'Free Redesign',
						sort: true,
						type: 'boolean',
						yes: 'yes',
						no: 'no',
					},

					{
						name: 'theme',
						label: 'Theme',
						sort: true,
						type: 'default',
					},
					{
						name: 'package',
						label: 'package',
						sort: false,
						type: 'default',
					},
					{
						name: 'hosting_server',
						label: 'Hosting server',
						sort: false,
						type: 'default',
					},

					{
						name: 'main_wp',
						label: 'Main WP',
						sort: true,
						type: 'boolean',
						yes: 'yes',
						no: 'no',
					},

					{
						name: 'security',
						label: 'Security',
						sort: true,
						type: 'boolean',
						yes: 'yes',
						no: 'no',
					},

					{
						name: 'live_stream',
						label: 'Live stream',
						sort: true,
						type: 'boolean',
						yes: 'yes',
						no: 'no',
					},

					{
						name: 'main_wp_id',
						label: 'MainWP ID',
						sort: false,
						type: 'default',
					},
					{
						name: 'client_since',
						label: 'Client Since',
						sort: false,
						type: 'default',
					},
					{
						name: 'ts',
						label: 'TS',
						sort: false,
						type: 'default',
					},
					{
						name: 'contract_info_updated_at',
						label: 'Contract info updated',
						sort: false,
						type: 'default',
					},
					{
						name: 'contract_start_at',
						label: 'Contract Start',
						sort: false,
						type: 'default',
					},

					{
						name: 'contract_ends_at',
						label: 'Contract End',
						sort: false,
						type: 'default',
					},

					{
						name: 'dns_holder',
						label: 'DNS Holder',
						sort: false,
						type: 'default',
					},

					{
						name: 'dns_holder_id',
						label: 'DNS Holder ID',
						sort: false,
						type: 'default',
					},

					{
						name: 'deleted_at',
						label: 'Deleted',
						sort: false,
						type: 'default',
					},
					{
						name: 'white_glove_service',
						label: 'WGS',
						sort: false,
						type: 'default',
					},
					{
						name: 'created_at',
						label: 'Created',
						sort: false,
						type: 'default',
					},
					{
						name: 'updated_at',
						label: 'Updated',
						sort: false,
						type: 'default',
					},
					{
						name: 'actions',
						label: 'Actions',
						sort: false,
						type: 'actions',
						width: 150,
						buttons: [
							{
								type: 'default',
								label: 'View',
								action: '',
								class: 'btn-secondary',
								permission: `CLIENT_ACCESS`,
							},
							{
								type: 'default',
								label: 'Edit',
								action: '',
								class: 'btn-primary',
								permission: `CLIENT_UPDATE`,
							},
							{
								type: 'delete',
								label: 'Delete',
								action: '',
								class: 'btn-danger',
								permission: `CLIENT_DELETE`,
							},
						],
					},
				],
				filters: [
					{
						display: true,
						name: 'search',
						label: 'Search',
						type: 'text',
						placeholder: 'enter text and press enter',
						colClass: 'col-4',
						value: this.$route.query.search || '',
					},
					{
						display: true,
						name: 'type',
						label: 'Type',
						type: 'select',
						placeholder: 'All types',
						colClass: 'col-2',
						value: this.$route.query.type || '',
						options: [
							{
								id: 'Business',
								name: 'Business',
							},
							{
								id: 'Non Profit',
								name: 'Non Profit',
							},
							{
								id: 'Lake District',
								name: 'Lake District',
							},
							{
								id: 'Municipality',
								name: 'Municipality',
							},
							{
								id: 'Other',
								name: 'Other',
							},
							{
								id: 'Fire Department',
								name: 'Fire Department',
							},
							{
								id: 'Police Department',
								name: 'Police Department',
							},
							{
								id: 'Chamber',
								name: 'Chamber',
							},
							{
								id: 'United Way',
								name: 'United Way',
							},
						],
					},
					{
						display: true,
						name: 'status',
						label: 'Status',
						type: 'select',
						placeholder: 'All statuses',
						colClass: 'col-2',
						value: this.$route.query.status || '',
						options: [
							{
								id: 'active',
								name: 'Active',
							},
							{
								id: 'deleted',
								name: 'Lost customer',
							},
							{
								id: 'suspended',
								name: 'Suspended',
							},
						],
					},
					{
						display: true,
						name: 'cms_version',
						label: 'CMS Version',
						type: 'select',
						placeholder: 'All versions',
						colClass: 'col-2',
						value: this.$route.query.cms_version || '',
						options: [
							{
								id: 'juniper',
								name: 'Juniper',
							},
							{
								id: 'v1',
								name: 'v1',
							},
							{
								id: 'v2',
								name: 'v2',
							},
							{
								id: 'v3',
								name: 'v3',
							},
							{
								id: 'v4',
								name: 'v4',
							},
							{
								id: 'v5 Wordpress',
								name: 'v5 Wordpress',
							},
							{
								id: 'Custom Design',
								name: 'Custom Design',
							},
						],
					},
					{
						display: true,
						name: 'contract_type',
						label: 'Contract Type',
						type: 'select',
						placeholder: 'All types',
						colClass: 'col-2',
						value: this.$route.query.contract_type || '',
						options: [
							{
								id: '1 year',
								name: '1 year',
							},
							{
								id: '2 years',
								name: '2 years',
							},
							{
								id: '3 years',
								name: '3 years',
							},
							{
								id: '4 years',
								name: '4 years',
							},
							{
								id: '5 years',
								name: '5 years',
							},

							{
								id: 'No contract',
								name: 'No contract',
							},
						],
					},
					{
						display: true,
						name: 'theme',
						label: 'Theme',
						type: 'select',
						placeholder: 'All themes',
						colClass: 'col-3',
						value: this.$route.query.contract_type || '',
						options: [
							{
								id: 'Premier',
								name: 'Premier',
							},
							{
								id: 'Juniper Maverick',
								name: 'Juniper Maverick',
							},
							{
								id: 'Juniper Lightyear',
								name: 'Juniper Lightyear',
							},
							{
								id: 'Juniper Mockingbird',
								name: 'Juniper Mockingbird',
							},
							{
								id: '2022 Theme',
								name: '2022 Theme',
							},
							{
								id: '2022 Mini Theme',
								name: '2022 Mini Theme',
							},
							{
								id: 'TW 2020',
								name: 'TW 2020',
							},
							{
								id: 'TW 2019',
								name: 'TW 2019',
							},
							{
								id: 'TW 2018',
								name: 'TW 2018',
							},
							{
								id: 'Rural Rustic',
								name: 'Rural Rustic',
							},
							{
								id: 'NY Super Theme',
								name: 'NY Super Theme',
							},
							{
								id: 'Reclaimed Modern',
								name: 'Reclaimed Modern',
							},
							{
								id: 'Urban Modern',
								name: 'Urban Modern',
							},
							{
								id: 'Rural Refined',
								name: 'Rural Refined',
							},
							{
								id: 'The Thrill is Gone - OGv5',
								name: 'The Thrill is Gone - OGv5',
							},
							{
								id: 'Custom Design',
								name: 'Custom Design',
							},
							{
								id: 'Other',
								name: 'Other',
							},
						],
					},
					{
						display: true,
						name: 'state',
						label: 'State',
						type: 'select',
						placeholder: 'All states',
						colClass: 'col-2',
						options: [],
						optionValue: true,
						optionsEndpoint: `municipalities/states`,
						value: this.$route.query.state || [],
					},
					/*{
						display: true,
						name: 'county',
						label: 'County',
						type: 'select',
						placeholder: 'All counties',
						colClass: 'col-2',
						options: [],
						optionValue: true,
						optionsEndpoint: `municipalities/counties`,
						value: this.$route.query.county || [],
					},
					*/

					{
						display: true,
						name: 'tw_client',
						label: 'Town Web client',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.tw_client || '',
						options: [
							{
								id: 'no',
								name: 'No',
							},
							{
								id: 'yes',
								name: 'Yes',
							},
						],
					},
					{
						display: true,
						name: 'tk_client',
						label: 'Tweak client',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.tk_client || '',
						options: [
							{
								id: 'no',
								name: 'No',
							},
							{
								id: 'yes',
								name: 'Yes',
							},
						],
					},
					{
						display: true,
						name: 'hg_client',
						label: 'HeyGov Client',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.hg_client || '',
						options: [
							{
								id: 'no',
								name: 'No',
							},
							{
								id: 'yes',
								name: 'Yes',
							},
						],
					},
					{
						display: true,
						name: 'hosting',
						label: 'Hosting',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.hosting || '',
						options: [
							{
								id: 'no',
								name: 'No',
							},
							{
								id: 'yes',
								name: 'Yes',
							},
						],
					},
					{
						display: true,
						name: 'branded_emails',
						label: 'Branded emails',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.branded_emails || '',
						options: [
							{
								id: 'no',
								name: 'No',
							},
							{
								id: 'yes,not implemented',
								name: 'Yes, not implemented',
							},
							{
								id: 'yes,not implemented,archiving',
								name: 'Yes, not implemented,archiving',
							},
							{
								id: 'yes,implemented',
								name: 'Yes, implemented',
							},
							{
								id: 'yes,implemented,archiving',
								name: 'Yes, implemented,archiving',
							},
						],
					},
					{
						display: true,
						name: 'online_payments',
						label: 'Online payments',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.online_payments || '',
						options: [
							{
								id: 'no',
								name: 'No',
							},
							{
								id: 'yes,not implemented',
								name: 'Yes, not implemented',
							},
							{
								id: 'yes,implemented',
								name: 'Yes, implemented',
							},
						],
					},
					{
						display: true,
						name: 'free_redesign',
						label: 'Free redesign',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.free_redesign || '',
						options: [
							{
								id: 'no',
								name: 'No',
							},
							{
								id: 'yes',
								name: 'Yes',
							},
						],
					},
					{
						display: true,
						name: 'package',
						label: 'Package',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.package || '',
						options: [
							{
								id: 'Other',
								name: 'Other',
							},
							{
								id: 'Select',
								name: 'Select',
							},
							{
								id: 'Deluxe',
								name: 'Deluxe',
							},
							{
								id: 'Super Deluxe',
								name: 'Super Deluxe',
							},
							{
								id: 'Rural',
								name: 'Rural',
							},
							{
								id: 'Select Plus',
								name: 'Select Plus',
							},
							{
								id: 'Elite',
								name: 'Elite',
							},

							{
								id: 'Essential',
								name: 'Essential',
							},
							{
								id: 'Community Foundation',
								name: 'Community Foundation',
							},
							{
								id: 'Community Foundation with Payments',
								name: 'Community Foundation with Payments',
							},
						],
					},
					{
						display: true,
						name: 'facebook_syndication',
						label: 'Facebook syndication',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.facebook_syndication || '',
						options: [
							{
								id: 'no',
								name: 'No',
							},
							{
								id: 'yes,not implemented',
								name: 'Yes, not implemented',
							},
							{
								id: 'yes,implemented',
								name: 'Yes, implemented',
							},
						],
					},
					{
						display: true,
						name: 'live_stream',
						label: 'Live stream',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.live_stream || '',
						options: [
							{
								id: 'no',
								name: 'No',
							},
							{
								id: 'yes,not implemented',
								name: 'Yes, not implemented',
							},
							{
								id: 'yes,implemented',
								name: 'Yes, implemented',
							},
						],
					},
					{
						display: true,
						name: 'client_since',
						label: 'Client since',
						type: 'daterange',
						placeholder: '',
						colClass: 'col-4',
						from: this.$route.query.fromclient_since || '',
						to: this.$route.query.toclient_since || '',
					},
					{
						display: true,
						name: 'population',
						label: 'Population',
						type: 'range',
						placeholder: '',
						colClass: 'col-3',
						min: 0,
						max: 99999999,
						from: this.$route.query.frompopulation || '',
						to: this.$route.query.topopulation || '',
					},
					{
						display: true,
						name: 'heygov_311',
						label: 'HeyGov 311',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.heygov_311 || '',
						options: [
							{
								id: 'no',
								name: 'No',
							},
							{
								id: 'yes',
								name: 'Yes',
							},
						],
					},
					{
						display: true,
						name: 'heygov_reserve',
						label: 'HeyGov Reserve',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.heygov_reserve || '',
						options: [
							{
								id: 'no',
								name: 'No',
							},
							{
								id: 'yes',
								name: 'Yes',
							},
						],
					},
					{
						display: true,
						name: 'heygov_license',
						label: 'HeyGov License',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.heygov_license || '',
						options: [
							{
								id: 'no',
								name: 'No',
							},
							{
								id: 'yes',
								name: 'Yes',
							},
						],
					},
					{
						display: true,
						name: 'heygov_pay',
						label: 'HeyGov Pay',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.heygov_pay || '',
						options: [
							{
								id: 'no',
								name: 'No',
							},
							{
								id: 'yes',
								name: 'Yes',
							},
						],
					},
					{
						display: true,
						name: 'heygov_clerk_minutes',
						label: 'HeyGov ClerkMinutes',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.heygov_clerk_minutes || '',
						options: [
							{
								id: 'no',
								name: 'No',
							},
							{
								id: 'yes',
								name: 'Yes',
							},
						],
					},
					{
						// Vanteve is a lifecycle, not a yes/no product tick — the options are its four states
						display: true,
						name: 'heygov_vanteve',
						label: 'Vanteve',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.heygov_vanteve || '',
						options: [
							{
								id: 'beta_tester',
								name: 'Beta tester',
							},
							{
								id: 'trial',
								name: 'Trial',
							},
							{
								id: 'active',
								name: 'Active',
							},
							{
								id: 'canceled',
								name: 'Canceled',
							},
						],
					},
					{
						display: true,
						name: 'white_glove_service',
						label: 'WGS',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.white_glove_service || '',
						options: [
							{
								id: 'Yes, email only',
								name: 'Yes, email only',
							},
							{
								id: 'Yes, email+phone',
								name: 'Yes, email+phone',
							},
							{
								id: 'no',
								name: 'No',
							},
						],
					},
					{
						display: true,
						name: 'ada_premium',
						label: 'ADA Premium',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.ada_premium || '',
						options: [
							{
								id: 'Yes',
								name: 'Yes',
							},
							{
								id: 'No',
								name: 'No',
							},
						],
					},
					{
						display: true,
						name: 'logo_option',
						label: 'Logo',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.logo_option || '',
						options: [
							{
								id: 'Free',
								name: 'Free',
							},
							{
								id: 'Paid',
								name: 'Paid',
							},
							{
								id: 'N/A',
								name: 'N/A',
							},
						],
					},
					{
						display: true,
						name: 'dev_status',
						label: 'Dev Status',
						type: 'select',
						placeholder: 'Select any',
						colClass: 'col-2',
						value: this.$route.query.dev_status || '',
						options: [
							{
								id: 'Live',
								name: 'Live',
							},
							{
								id: 'New - In Progress',
								name: 'New - In Progress',
							},
							{
								id: 'Switch - In Progress',
								name: 'Switch - In Progress',
							},
							{
								id: 'Upgrade - In Progress',
								name: 'Upgrade - In Progress',
							},
						],
					},
				],
			},
			params: {},
			userColumns: JSON.parse(localStorage.getItem(`client-columns`)) || ['id', 'type', 'name', 'state', 'county', 'status', 'cms_version', 'actions'],
			availableColumns: ['id'],
			searchName: '',
			searchSaving: false,
			searchActive: '',
			searches: [],
			newExport: {
				type: 'csv',
				columnsType: 'visible',
				filename: '',
				availableColumns: ['id'],
				exportColumns: JSON.parse(localStorage.getItem(`client-columns`)) || ['id', 'type', 'name', 'state', 'county', 'status', 'cms_version', 'actions'],
			},
			loadExporting: false,
			exportingFile: '',
			$clientInfoModal: null,
			clientInfo: {},
			newClient: {
				name: '',
				type: '',
				municipality_id: null,
				contract_type: '',
				primary_product: '',
				county: null,
				address: null,
				zip: null,
				state: null,
				state_abb: null,
				contract_start_at: null,
			},
			addClients: false,
			municipalityContacts: [],
			activeContact: {
				first_name: '',
				last_name: '',
				email: '',
				phone: '',
				email_secondary: '',
				phone_secondary: '',
				address: '',
				title: '',
				client_id: null,
			},
			activeContactOriginal: null,
			updateContactFlag: false,
			addContactFlag: false,
			updateAddressFlag: false,
			fips: '',
			$dataInfoModal: null,
			dataInfo: {},
			newCampaign: {
				name: '',
				description: '',
				utm_campaign: '',
				utm_content: '',
				filters: '',
				municipalities_count: '',
				source: 'clients',
				objects_type: 'client',
				campaignObjects: [],
			},
			loadingCampaignObjects: false,
			loadingCampaingCreate: false,
		}
	},
	computed: {
		...mapState(['authUser', 'storageUrl']),
	},
	created() {
		this.loadFiltersUiSettings()
		this.table.filters.forEach(filter => {
			if (filter.value && filter.value !== '' && filter.value.length > 0) {
				this.params[filter.name] = filter.value
			} else {
				delete this.params[filter.name]
			}

			if (filter.from && filter.from !== '' && filter.to && filter.to !== '') {
				this.params['from' + filter.name] = filter.from
				this.params['to' + filter.name] = filter.to
			} else {
				delete this.params['from' + filter.name]
				delete this.params['to' + filter.name]
			}
		})
		this.getTableColumns()
		this.getTableData(true)
		this.getSearches()
	},
	mounted() {
		this.$createModal = new Modal('#create')
		this.$addFilterModal = new Modal('#addFilter')
		this.$exportModal = new Modal('#exportModal')
		this.$clientInfoModal = new Modal('#clientInfo')
		this.$dataInfoModal = new Modal('#dataInfoModal')
		this.$campaignModal = new Modal('#campaignModal')
	},
	methods: {
		async loadFiltersUiSettings() {
			try {
				const { data } = await restApi.get('/settings', { params: { name: 'clientsFiltersUi', type: 'settings' } })
				if (data && data.data) {
					this.filtersUiSettings = JSON.parse(data.data)
					if (!this.filtersUiSettings.default) {
						this.filtersUiSettings.default = 'default'
					}
					if (!this.filtersUiSettings.overrides) {
						this.filtersUiSettings.overrides = {}
					}
				}
			} catch (error) {
				console.error(error)
			} finally {
				const override = this.filtersUiSettings.overrides?.[this.authUser.id]
				const overrideMode = typeof override === 'string' ? override : override?.mode
				this.filtersUiMode = overrideMode || this.filtersUiSettings.default || 'default'
			}
		},
		// Get table Columns
		getTableColumns() {
			restApi
				.post('/helper/table-columns', { table: this.route })
				.then(({ data }) => {
					this.newExport.availableColumns = data.map(column => column.name)
					this.availableColumns = data.map(column => column.name)
					this.availableColumns.push('actions', 'population')
					this.newExport.availableColumns.push('population')
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},
		// Get table data from API
		getTableData(first = false) {
			this.loading = true
			this.params = {
				...this.params,
				page: this.page,
				orderBy: this.orderBy,
				direction: this.direction,
				perPage: localStorage.getItem(`${this.model}-per-page`) || 20,
			}
			restApi
				.get(`/${this.route}`, { params: this.params })
				.then(({ data }) => {
					this.tableData = data.data
					this.pagination = data
					delete this.pagination.data
					this.loading = false
					if (!first) {
						this.$router.push({ path: this.$router.currentPath, query: this.params })
					}
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Adding new filter
		addFilter() {
			this.$addFilterModal.hide()
		},

		// Go to user page
		viewUser(user) {
			this.$router.push('/users/' + user.id)
		},

		// Get items from page that returned from LPDataTablePagination
		getItems(page) {
			this.page = page
			this.getTableData()
		},

		// Set direction
		setOrderBy(orderBy, direction) {
			this.orderBy = orderBy
			this.direction = direction
			this.getTableData()
		},

		// Get filters from LPDataTableFilters component
		getFilters(filters) {
			this.page = ''
			this.searchActive = ''
			filters.forEach(filter => {
				if (filter.value && filter.value !== '' && filter.value.length > 0) {
					this.params[filter.name] = filter.value
				} else {
					delete this.params[filter.name]
				}
				// Setting filter if its type is range
				if (filter.from && filter.from !== '' && filter.to && filter.to !== '') {
					this.params['from' + filter.name] = filter.from
					this.params['to' + filter.name] = filter.to
				} else {
					delete this.params['from' + filter.name]
					delete this.params['to' + filter.name]
				}
			})
			this.getTableData()
		},

		// Toggle user column
		toggleUserColumn(column) {
			if (this.userColumns.includes(column)) {
				this.userColumns = this.userColumns.filter(c => c !== column)
			} else {
				this.userColumns.push(column)
			}
			this.updateUserColumns()
		},

		// Update user columns
		updateUserColumns() {
			localStorage.setItem(`${this.model}-columns`, JSON.stringify(this.userColumns))
		},

		// Get user saved searches
		getSearches() {
			let params = {
				user_id: this.authUser.id,
				model: this.model,
			}
			restApi
				.get('/searches', { params })
				.then(({ data }) => {
					data.forEach(d => {
						d.data = JSON.parse(d.data)
						return d
					})
					this.searches = data
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Enable saving if some filters are set
		haveParamsToSave() {
			if (Object.values(this.params).length > 4) {
				return true
			} else {
				return false
			}
		},

		// Get user saved searches
		storeSearch() {
			let dataParams = this.params
			restApi
				.post('/searches', {
					user_id: this.authUser.id,
					model: `${this.model}`,
					name: this.searchName,
					data: JSON.stringify(dataParams),
				})
				.then(({ data }) => {
					data.data = JSON.parse(data.data)
					this.searches.push(data)
					this.searchActive = this.searchName
					this.searchSaving = false
					this.$swal.fire({
						icon: 'success',
						title: 'Search successfully saved!',
						timer: 4000,
					})
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Apply saved search to table
		applySearch(search) {
			// Reset all before applying saved search
			this.params = {}
			this.table.filters.forEach(filter => {
				if (filter.type !== 'range' && filter.type !== 'daterange') {
					filter.value = ''
				} else {
					filter.from = ''
					filter.to = ''
				}
			})
			// Apply saved search
			this.params = search.data
			let paramsArray = Object.keys(search.data)
			this.table.filters.forEach(filter => {
				if (paramsArray.includes(filter.name) && filter.type !== 'range') {
					filter.value = this.params[filter.name]
				}
				if (paramsArray.includes('from' + filter.name) && filter.type === 'range') {
					filter.from = this.params['from' + filter.name]
					filter.to = this.params['to' + filter.name]
				}
				if (paramsArray.includes('from' + filter.name) && filter.type === 'daterange') {
					filter.from = this.params['from' + filter.name]
					filter.to = this.params['to' + filter.name]
				}
			})
			this.searchActive = search.name
			this.getTableData()
		},

		// Clear all filters
		clearFilters() {
			this.params = {}
			this.searchActive = ''
			this.table.filters.forEach(filter => {
				if (filter.type !== 'range' && filter.type !== 'multiselect') {
					filter.value = ''
				}
				if (filter.type === 'multiselect') {
					filter.value = []
				}
				if (filter.type === 'range' || filter.type === 'daterange') {
					filter.from = ''
					filter.to = ''
				}
			})
			this.getTableData()
		},

		// Toggle export column
		toggleExportColumn(column) {
			if (this.newExport.exportColumns.includes(column)) {
				this.newExport.exportColumns = this.newExport.exportColumns.filter(c => c !== column)
			} else {
				this.newExport.exportColumns.push(column)
			}
		},

		// Export table Data
		exportData() {
			this.loadExporting = true
			this.newExport.exportColumns = this.newExport.exportColumns.filter(column => column !== 'actions')
			restApi
				.post(`/${this.route}/export`, {
					params: JSON.stringify(this.params),
					columns: JSON.stringify(this.newExport.exportColumns),
					filename: this.newExport.filename,
				})
				.then(({ data }) => {
					this.loadExporting = false
					this.exportingFile = this.storageUrl + '/tmp/' + data.file
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Download exported Data
		downloadExport() {
			this.$exportModal.hide()
			this.newExport.filename = ''
			this.exportingFile = ''
		},

		// Set user per page
		setUserPerPage() {
			localStorage.setItem(`${this.model}-per-page`, this.userPerPage)
			this.getTableData()
		},

		showModal(data) {
			this.clientInfo = data
			this.$clientInfoModal.show()
		},

		setMunicipality(municipality) {
			if (municipality !== null) {
				this.fips = municipality.fips
				this.newClient.municipality_id = municipality.id
				this.newClient.state_abb = municipality.state
				this.getStateName(municipality.state)
				this.newClient.county = municipality.county
				this.newClient.address = municipality.address_street
				this.newClient.zip = municipality.zip
				this.newClient.name = municipality.full_name
				this.municipalityContacts = municipality.contacts
			} else {
				this.newClient.name = ''
				this.newClient.municipality_id = null
				this.newClient.contract_type = ''
				this.newClient.primary_product = ''
				this.newClient.county = null
				this.newClient.address = null
				this.newClient.zip = null
				this.newClient.state = null
				this.newClient.state_abb = null
				this.newClient.contract_start_at = null
			}
		},

		// Get full state name from Abbreviation
		getStateName(value) {
			this.params = {
				value: value,
			}
			restApi
				.get(`/helper/state-name`, { params: this.params })
				.then(({ data }) => {
					this.newClient.state = data
				})
				.catch(error => {
					this.loading = false
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		clearNewClient() {
			if (this.newClient.type !== 'municipality') {
				this.newClient.name = ''
				this.newClient.municipality_id = null
				this.newClient.contract_type = ''
				this.newClient.primary_product = ''
				this.newClient.county = null
				this.newClient.address = null
				this.newClient.zip = null
				this.newClient.state = null
				this.newClient.state_abb = null
				this.newClient.contract_start_at = null
			}
		},

		// Disable next button for adding contacts
		disableNextButton() {
			let disable = false
			if (this.newClient.name.length < 1) {
				disable = true
			} else {
				disable = false
			}
			if (this.newClient.county === null) {
				disable = true
			} else {
				disable = false
			}
			if (this.newClient.address === null) {
				disable = true
			} else {
				disable = false
			}
			if (this.newClient.state === null) {
				disable = true
			}
			if (this.newClient.contract_start_at === null) {
				disable = true
			} else {
				disable = false
			}
			return disable
		},

		addClientContacts() {
			this.addClients = true
		},

		// Select existing contact from table and make updates if need but keep old values
		selectContact(contact) {
			this.municipalityContacts.forEach(c => {
				c.active = false
			})

			contact.active = true

			this.addContactFlag = false
			this.updateContactFlag = true
			this.activeContactOriginal = JSON.parse(JSON.stringify(contact))
			this.activeContact.first_name = contact.first_name
			this.activeContact.last_name = contact.last_name
			this.activeContact.email = contact.email
			this.activeContact.phone = contact.phone
			this.activeContact.address = contact.address
			this.activeContact.contact_id = contact.id
			this.activeContact.title = contact.title
		},

		addNewContact() {
			this.municipalityContacts.forEach(c => {
				c.active = false
			})
			this.addContactFlag = true
			this.updateContactFlag = false

			this.activeContact = {
				first_name: '',
				last_name: '',
				email: '',
				phone: '',
				email_secondary: '',
				phone_secondary: '',
				address: '',
				title: '',
				contact_id: null,
			}
		},

		cancelAddingClient() {
			this.activeContact = {
				first_name: '',
				last_name: '',
				email: '',
				phone: '',
				email_secondary: '',
				phone_secondary: '',
				address: '',
				title: '',
			}
			this.newClient = {
				name: '',
				type: '',
				municipality_id: null,
				contract_type: '',
				primary_product: '',
				county: null,
				address: null,
				zip: null,
				state: null,
				state_abb: null,
				contract_start_at: null,
			}
			this.addContactFlag = false
			this.updateContactFlag = false
			this.addClients = false
		},

		disableFinishButton() {
			let disable = false
			if (this.activeContact.first_name.length < 1) {
				disable = true
			} else {
				disable = false
			}
			if (this.activeContact.last_name.length < 1) {
				disable = true
			} else {
				disable = false
			}
			if (this.activeContact.email.length < 1) {
				disable = true
			} else {
				disable = false
			}
			return disable
		},

		// Create new Client with contact data
		create() {
			// Store new client
			restApi
				.post(`/clients`, this.newClient)
				.then(({ data }) => {
					this.activeContact.client_id = data.id
					this.activeContact.municipality_id = data.municipality_id
					this.activeContact.main = 1
					delete this.activeContact.active
					this.type = ''

					// If Contact is not from BI add it also there to keep information
					if (this.activeContact.contact_id === null) {
						// Store client to all clients table
						restApi
							.post(`/contacts`, this.activeContact)
							.then(({ data }) => {
								this.activeContact.contact_id = data.id
								// Store Client primary contact
								restApi
									.post(`/client-contacts`, this.activeContact)
									.then(() => {
										this.$router.push(`/clients/edit/${this.activeContact.client_id}`)
										this.$createModal.hide()
										this.cancelAddingClient()
									})
									.catch(error => {
										this.$swal.fire({
											icon: 'error',
											title: handleErrors(error, ''),
											timer: 6000,
										})
									})
							})
							.catch(error => {
								this.$swal.fire({
									icon: 'error',
									title: handleErrors(error, ''),
									timer: 6000,
								})
							})
					} else {
						// Store Client primary contact
						restApi
							.post(`/client-contacts`, this.activeContact)
							.then(() => {
								this.$createModal.hide()
								// Update contact information
								if (this.updateContactFlag) {
									restApi
										.put(`/contacts/${this.activeContact.contact_id}`, this.activeContact)
										.then(({ data }) => {
											this.activeContact.contact_id = data.id
											this.$router.push(`/clients/edit/${this.activeContact.client_id}`)
											this.cancelAddingClient()
										})
										.catch(error => {
											this.$swal.fire({
												icon: 'error',
												title: handleErrors(error, ''),
												timer: 6000,
											})
										})
								}
							})
							.catch(error => {
								this.$swal.fire({
									icon: 'error',
									title: handleErrors(error, ''),
									timer: 6000,
								})
							})
					}

					this.$router.push('/clients/edit/' + this.activeContact.client_id)
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		showInformationModal(event) {
			restApi
				.get(`/${event.route}/${event.id}`)
				.then(({ data }) => {
					this.dataInfo = data
					this.$dataInfoModal.show()
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
					this.loading = false
				})
		},

		moreDetails() {
			this.$dataInfoModal.hide()
			this.$router.push('/clients/edit/' + this.dataInfo.id)
		},

		dateDifferenceDays(date) {
			return differenceInDays(new Date(date), new Date())
		},

		// Get all filtered clients for campaign
		getCampaignClients() {
			this.loadingCampaignObjects = true
			this.params = {
				...this.params,
				page: 1,
				orderBy: this.orderBy,
				direction: this.direction,
				perPage: 20000,
			}
			restApi
				.get(`/${this.route}`, { params: this.params })
				.then(({ data }) => {
					const campaignClients = Array.isArray(data.data) ? data.data.map(client => client.id) : []
					this.newCampaign.campaignObjects = campaignClients
					this.loadingCampaignObjects = false
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},

		// Create campaign
		createCampaign() {
			this.loadingCampaingCreate = true
			this.newCampaign.filters = JSON.stringify(this.params)
			this.newCampaign.municipalities_count = this.pagination.total
			// Store new campaign
			restApi
				.post(`/campaigns`, this.newCampaign)
				.then(({ data }) => {
					this.loadingCampaingCreate = false
					this.$campaignModal.hide()
					this.$router.push(`/campaigns/edit/${data.id}`)
				})
				.catch(error => {
					console.log(error)
					this.$swal.fire({
						icon: 'error',
						title: handleErrors(error, ''),
						timer: 6000,
					})
				})
		},
	},
	watch: {
		municipalityContacts: function() {
			if (this.municipalityContacts.length === 0) {
				this.addContactFlag = true
			} else {
				this.addContactFlag = false
			}
		},
		addClients: function() {
			if (this.newClient !== 'municipality' && this.municipalityContacts.length < 1) {
				this.addContactFlag = true
			}
		},
	},
}
</script>

<style lang="scss" scoped>
.contact-item {
	&:hover {
		background-color: whitesmoke;
	}
}
</style>
